import React, { useState } from 'react';
import { Modal, Form, Alert, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { useSacar } from 'hooks/useContas';
import { ContaType, SacarDto } from 'services/contaService';

interface ModalSacarProps {
    show: boolean;
    onHide: () => void;
    conta: ContaType | null;
}

const ModalSacar: React.FC<ModalSacarProps> = ({ show, onHide, conta }) => {
    const [valor, setValor] = useState('');
    const [descricao, setDescricao] = useState('');
    const [erro, setErro] = useState('');

    const sacarMutation = useSacar();

    const formatarMoeda = (valor: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErro('');

        if (!valor || parseFloat(valor) <= 0) {
            setErro('O valor deve ser maior que zero');
            return;
        }

        if (!conta) {
            setErro('Conta não encontrada');
            return;
        }

        const valorNumerico = parseFloat(valor);
        if (valorNumerico > conta.saldo) {
            setErro('Saldo insuficiente para realizar o saque');
            return;
        }

        try {
            const dto: SacarDto = {
                contaId: conta.id,
                valor: valorNumerico,
                descricao: descricao.trim() || undefined
            };
            await sacarMutation.mutateAsync(dto);

            setValor('');
            setDescricao('');
            onHide();
        } catch (error: any) {
            setErro(error?.response?.data?.message || 'Erro ao realizar saque');
        }
    };

    const handleClose = () => {
        setValor('');
        setDescricao('');
        setErro('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faArrowUp} className="me-2 text-danger-emphasis" />
                    Sacar
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {erro && (
                        <Alert variant="danger" className="mb-3 border border-danger border-opacity-25 bg-danger-subtle">
                            <div className="text-danger-emphasis">{erro}</div>
                        </Alert>
                    )}

                    {conta && (
                        <Alert variant="info" className="mb-3 border border-info border-opacity-25 bg-info-subtle">
                            <div className="text-info-emphasis">
                                <strong>{conta.nome}</strong><br />
                                Saldo disponível: <span className="text-success-emphasis">{formatarMoeda(conta.saldo)}</span>
                            </div>
                        </Alert>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Valor do Saque *</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>R$</InputGroup.Text>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={valor}
                                onChange={(e) => setValor(e.target.value)}
                                placeholder="0,00"
                                required
                                disabled={sacarMutation.isPending}
                            />
                        </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Descrição (opcional)</Form.Label>
                        <Form.Control
                            type="text"
                            value={descricao}
                            onChange={(e) => setDescricao(e.target.value)}
                            placeholder="Ex: Saque para despesas, pagamento..."
                            disabled={sacarMutation.isPending}
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={sacarMutation.isPending}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="danger"
                        type="submit"
                        disabled={sacarMutation.isPending || !valor || parseFloat(valor || '0') <= 0}
                    >
                        {sacarMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Processando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faArrowUp} className="me-2" />
                                Confirmar Saque
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ModalSacar; 